import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  getPublicMobileCheckout,
  startPublicMobileCheckoutPayment,
  type PublicMobileCheckout as PublicMobileCheckoutData,
} from '../api/client';

function formatAmount(amount: number, currency: string) {
  return amount.toLocaleString('he-IL', { style: 'currency', currency: currency || 'ILS' });
}

// Israeli mobile numbers: 10 digits starting with 05, or +972 5X form.
function isValidPhone(raw: string): boolean {
  const digits = raw.replace(/[^\d]/g, '');
  if (digits.startsWith('972')) return digits.length === 12 && digits[3] === '5';
  return digits.length === 10 && digits.startsWith('05');
}

export default function PublicMobileCheckout() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [checkout, setCheckout] = useState<PublicMobileCheckoutData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [fieldError, setFieldError] = useState<{ name?: string; phone?: string }>({});

  useEffect(() => {
    setLoading(true);
    setError(null);
    getPublicMobileCheckout(token)
      .then((res) => setCheckout(res.data))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 410) setError(t('publicMobileCheckout.expired'));
        else setError(err?.response?.data?.detail || t('publicMobileCheckout.unavailable'));
      })
      .finally(() => setLoading(false));
  }, [token]);

  const isExpired = useMemo(
    () => checkout?.status === 'expired' || checkout?.status === 'revoked',
    [checkout?.status],
  );

  const requiresDetails = !!checkout?.requires_payer_details;

  const validate = (): boolean => {
    if (!requiresDetails) return true;
    const next: { name?: string; phone?: string } = {};
    if (!name.trim()) next.name = t('publicMobileCheckout.nameRequired');
    if (!isValidPhone(phone)) next.phone = t('publicMobileCheckout.phoneRequired');
    setFieldError(next);
    return Object.keys(next).length === 0;
  };

  const pay = async () => {
    if (!validate()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await startPublicMobileCheckoutPayment(
        token,
        requiresDetails ? { name: name.trim(), phone: phone.trim() } : {},
      );
      if (res.data.payment_url) {
        setRedirecting(true);
        window.location.href = res.data.payment_url;
      } else {
        setError(t('publicMobileCheckout.startPaymentError'));
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 410) setError(t('publicMobileCheckout.expired'));
      else setError(err?.response?.data?.detail || t('publicMobileCheckout.startPaymentError'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-gray-600">
        {t('publicMobileCheckout.loading')}
      </div>
    );
  }

  if (error && !checkout) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">{t('publicMobileCheckout.unavailableTitle')}</h1>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!checkout) return null;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <main className="mx-auto max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="border-b border-gray-100 pb-4 text-center">
          <h1 className="text-xl font-semibold text-gray-900">{checkout.business_name}</h1>
        </div>

        <div className="mt-5 rounded-lg bg-gray-50 p-4 text-center">
          <div className="text-sm text-gray-600">{t('publicMobileCheckout.amountToPay')}</div>
          <div className="mt-1 text-3xl font-bold text-gray-900">
            {formatAmount(checkout.amount_ils, checkout.currency)}
          </div>
          {checkout.description && (
            <div className="mt-1 text-sm text-gray-500">{checkout.description}</div>
          )}
        </div>

        {isExpired ? (
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-center text-sm text-amber-800">
            {t('publicMobileCheckout.expired')}
          </div>
        ) : (
          <>
            {requiresDetails && (
              <div className="mt-6 space-y-4">
                <h2 className="text-sm font-semibold text-gray-700">{t('publicMobileCheckout.yourDetails')}</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="payer-name">
                    {t('publicMobileCheckout.nameLabel')}
                  </label>
                  <input
                    id="payer-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('publicMobileCheckout.namePlaceholder')}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none"
                  />
                  {fieldError.name && <p className="mt-1 text-xs text-red-600">{fieldError.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="payer-phone">
                    {t('publicMobileCheckout.phoneLabel')}
                  </label>
                  <input
                    id="payer-phone"
                    type="tel"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t('publicMobileCheckout.phonePlaceholder')}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none"
                  />
                  {fieldError.phone && <p className="mt-1 text-xs text-red-600">{fieldError.phone}</p>}
                  <p className="mt-1 text-xs text-gray-500">{t('publicMobileCheckout.confirmHint')}</p>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            <button
              type="button"
              onClick={pay}
              disabled={busy || redirecting}
              className="mt-6 w-full rounded-lg bg-green-600 px-5 py-3 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              {redirecting
                ? t('publicMobileCheckout.redirecting')
                : busy
                ? t('publicMobileCheckout.processing')
                : t('publicMobileCheckout.pay')}
            </button>
          </>
        )}
      </main>
    </div>
  );
}
